import { z } from 'zod';
import { businessShapeKindSchema } from './brand-brain.js';
// (mag-built-marketing-sites, Phase 1) — the site IR + blueprint.
//
// `SiteBlueprint` is the proven section SEQUENCE for an archetype, derived
// offline from real best-in-class sites then curated ("A based on B" —
// validated by the blueprint spike). `BrandSite` is the generated site: the
// same section kinds, filled with this brand's copy + assets.
/** Canonical section kinds, shared by the blueprint (which sections, in
 *  order, required) and the generated site (the same kinds with content).
 *  Names match the spike-derived SaaS blueprint, generalized. */
export const siteSectionKindSchema = z.enum([
    'hero',
    'social_proof',
    'problem',
    'value_props',
    'product_demo',
    'manifesto',
    'testimonials',
    'pricing',
    'trust',
    'faq',
    'cta',
    'footer',
]);
// ─── SiteBlueprint (the curated, archetype-keyed sequence) ────────────
export const siteBlueprintSectionSchema = z.object({
    kind: siteSectionKindSchema,
    /** What this section accomplishes for this archetype. */
    purpose: z.string().max(400).default(''),
    /** Why it converts here. */
    rationale: z.string().max(400).default(''),
    /** Essential vs optional. The generator may drop an optional section
     *  the brain can't support; a required section it can't fill is
     *  recorded as a skip with a reason. */
    required: z.boolean().default(true),
});
export const siteBlueprintSchema = z.object({
    /** Keyed on identity.business_shape.kind so the generator matches a
     *  brand to its blueprint. */
    archetype: businessShapeKindSchema,
    /** 1-2 sentences on what these winners do structurally. */
    notes: z.string().max(600).default(''),
    sections: z.array(siteBlueprintSectionSchema).max(16).default([]),
});
// ─── BrandSite (the generated site) ───────────────────────────────────
/** A generic content item — one schema covers pillars, plans, faqs,
 *  testimonials, and product-demo steps via optional fields, rather than
 *  a verbose per-kind union. The renderer reads the fields relevant to
 *  its section kind. The generator is LLM-driven, so a flexible shape
 *  beats a rigid one here. */
export const brandSiteItemSchema = z.object({
    title: z.string().max(200).optional(),
    body: z.string().max(2000).optional(),
    // pricing plan
    price: z.string().max(120).optional(),
    highlights: z.array(z.string().max(200)).max(10).optional(),
    // testimonial
    quote: z.string().max(600).optional(),
    attribution: z.string().max(160).optional(),
    // faq
    question: z.string().max(300).optional(),
    answer: z.string().max(1200).optional(),
    // product-demo step / trust item
    label: z.string().max(120).optional(),
    description: z.string().max(1200).optional(),
});
export const brandSiteSectionSchema = z.object({
    id: z.string().min(1).max(40),
    kind: siteSectionKindSchema,
    /** Big line (hero headline, section heading, cta headline). */
    headline: z.string().max(280).optional(),
    /** Supporting line under a headline (hero subhead, section intro). */
    subhead: z.string().max(600).optional(),
    /** Paragraph blocks — problem narrative, founder manifesto. */
    body: z.array(z.string().max(2000)).max(8).default([]),
    /** Repeated items — value pillars, pricing plans, faqs, testimonials,
     *  product-demo steps, trust items. */
    items: z.array(brandSiteItemSchema).max(12).default([]),
    /** Hero / cta button labels. */
    primaryCta: z.string().max(80).optional(),
    secondaryCta: z.string().max(80).optional(),
    /** A brand asset (product screenshot / logo) selected for this section's
     *  visual slot — e.g. the hero shot or product_demo image. */
    imageAssetUrl: z.string().url().optional(),
});
// ─── SiteDesign (the art-director IR — Approach C, Phase 3) ────────────
//
// The `site-design` agent (Opus) emits this alongside the content IR: the
// model's design DECISIONS (bounded enums the renderer applies
// deterministically) + a small set of GENERATED SVG visuals/animations that
// fill the imagery gap. The model never writes page HTML/CSS — only these
// enums and SVG (a closed, sanitizable vocabulary, rendered in a
// non-scripting <img>/background context). See spec bba47dcf.
export const siteHeroLayoutSchema = z.enum(['left-bold', 'centered', 'split']);
export const siteSurfaceSchema = z.enum(['flat', 'tinted-bands', 'elevated-cards', 'glass']);
export const siteDensitySchema = z.enum(['tight', 'comfortable', 'airy']);
export const siteAccentIntensitySchema = z.enum(['subtle', 'medium', 'vivid']);
/** The design LANGUAGE — a hand-vetted CSS system the renderer applies over
 *  the stable section classes (Phase 5, spec bfdaef10). The site-design model
 *  SELECTS the family a top-performing site in this brand's industry would
 *  use, decoupled from the rigid business_shape archetype. `technical-grid`
 *  is the original SaaS skin (Linear/Vercel), so a family-less design renders
 *  exactly as before. Adding a family is additive — a new CSS string, no DOM
 *  change. */
export const siteDesignFamilySchema = z.enum([
    'technical-grid',
    'editorial-serif',
    'warm-dtc',
    'clinical-trust',
    'clinical-product',
    'luxe-minimal',
    'bold-brutalist',
]);
/** Where a generated visual is placed. `hero-backdrop` sits behind the hero
 *  copy; `hero-inline` is the split-hero visual column; `section` attaches
 *  to the first section of `sectionKind`; `motif` is a small repeating
 *  accent. */
export const siteVisualPlacementSchema = z.enum([
    'hero-backdrop',
    'hero-inline',
    'section',
    'motif',
]);
/** What KIND of imagery fills a visual slot (Phase 7, spec 2e27ca74). The
 *  site-design model art-directs this per slot: `svg` is a model-authored
 *  abstract/diagram graphic (the original behaviour), `photo` is real stock
 *  the worker fetches from Pexels by `query`, `generated` is a fal.ai image
 *  the worker renders from `prompt`. Defaults to `svg` so visuals stored
 *  before this layer (svg-only, no medium) parse unchanged. */
export const siteVisualMediumSchema = z.enum(['svg', 'photo', 'generated']);
export const siteVisualSchema = z.object({
    placement: siteVisualPlacementSchema,
    /** For placement:'section' — which section kind this visual decorates. */
    sectionKind: siteSectionKindSchema.optional(),
    /** Which kind of imagery fills this slot. */
    medium: siteVisualMediumSchema.default('svg'),
    /** Raw SVG from the model (medium='svg' only). Sanitized (parse +
     *  allow-list + re-serialize) before it ever reaches HTML; rejected
     *  visuals are dropped at render. Optional now that a slot may instead be
     *  a photo/generated image. */
    svg: z.string().max(24_000).optional(),
    /** Pexels search query (medium='photo') — concrete scene description, not
     *  marketing prose ("hands signing at a notary desk" > "professional"). */
    query: z.string().max(200).optional(),
    /** fal.ai prompt (medium='generated') — what to render. */
    prompt: z.string().max(600).optional(),
    /** The resolved, hosted image URL — filled by the worker's imagery
     *  resolver (R2 re-host of the Pexels pick or the fal.ai render). When
     *  set, the renderer injects this <img src>; when absent, it falls back to
     *  the svg data-URI (or drops the slot). */
    url: z.string().url().optional(),
    animated: z.boolean().default(false),
    /** Accessible description — generated visuals are decorative but still
     *  get alt text. */
    alt: z.string().max(200).default(''),
});
/** A token that CLAMPS (not rejects) an out-of-range number to its bounds,
 *  falls back to the default on a non-number, and defaults when absent — so
 *  a stray value from the model (e.g. 9999) tunes to the limit instead of
 *  throwing away the whole design. `round` keeps px tokens integral. */
function clampedToken(min, max, def, round = true) {
    return z
        .number()
        .catch(def)
        .transform((v) => {
        const c = Math.min(max, Math.max(min, v));
        return round ? Math.round(c) : c;
    })
        .default(def);
}
/** Bounded design tokens the model tunes (Phase 4) — injected as CSS
 *  custom properties the skin consumes. Every value is clamped to a safe
 *  range on parse, so the model (and the critique loop) can move
 *  composition without ever writing raw CSS. Defaults == the skin's
 *  hand-tuned values, so a token-less site renders identically. */
export const siteTokensSchema = z.object({
    /** Section vertical padding, px. */
    sectionSpacing: clampedToken(48, 168, 96),
    /** Max content column width, px. */
    contentWidth: clampedToken(880, 1280, 1080),
    /** Card / surface corner radius, px. */
    radius: clampedToken(0, 28, 16),
    /** Grid gap between cards, px. */
    cardGap: clampedToken(10, 40, 18),
    /** Hero top padding, px. */
    heroPadTop: clampedToken(72, 180, 120),
    /** Multiplier on display (heading) sizes — 1 = skin default. */
    displayScale: clampedToken(0.85, 1.3, 1, false),
    /** Cap on a section/inline generated-visual's height, px — the lever
     *  that fixes an oversized diagram floating in dead space. */
    visualMaxHeight: clampedToken(220, 760, 520),
});
const SITE_TOKENS_DEFAULTS = siteTokensSchema.parse({});
export const siteDesignSchema = z.object({
    /** The model's design reasoning — stored for iteration/debug, not rendered. */
    rationale: z.string().max(1600).default(''),
    /** The selected design language (Phase 5). Defaults to the original SaaS
     *  skin so sites generated before this layer render unchanged. */
    family: siteDesignFamilySchema.default('technical-grid'),
    /** Bounded design tokens (Phase 4) → CSS vars. */
    tokens: siteTokensSchema.default(SITE_TOKENS_DEFAULTS),
    hero: z
        .object({ layout: siteHeroLayoutSchema.default('left-bold') })
        .default({ layout: 'left-bold' }),
    surface: siteSurfaceSchema.default('tinted-bands'),
    density: siteDensitySchema.default('comfortable'),
    accentIntensity: siteAccentIntensitySchema.default('medium'),
    visuals: z.array(siteVisualSchema).max(8).default([]),
});
export const brandSiteStatusSchema = z.enum([
    'draft',
    // Async generation in flight (a worker is building/refining the site). Set
    // before enqueue; the row may carry the previous content (regenerate) or be
    // an empty placeholder (first generate). The dashboard polls on this.
    'generating',
    'generated',
    'edited',
    'published',
    // A generation job threw; the dashboard surfaces a retry. Carries the prior
    // content when a regenerate failed.
    'failed',
]);
export const brandSiteSchema = z.object({
    /** Which archetype blueprint produced this site. */
    blueprintArchetype: businessShapeKindSchema.nullable().default(null),
    sections: z.array(brandSiteSectionSchema).max(16).default([]),
    /** Blueprint sections the generator deliberately dropped + why (honest
     *  skips — e.g. "no testimonials: pre-revenue"). */
    skipped: z
        .array(z.object({ kind: siteSectionKindSchema, why: z.string().max(300) }))
        .max(16)
        .default([]),
    status: brandSiteStatusSchema.default('generated'),
    generatedAt: z.string().datetime().optional(),
    /** Art-director design layer (Phase 3): decisions + generated SVG visuals.
     *  Null on sites generated before the design layer, or when the design
     *  agent failed — the renderer falls back to the deterministic skin. */
    design: siteDesignSchema.nullable().default(null),
});
//# sourceMappingURL=brand-site.js.map